import { ErrorData } from 'hls.js';
import { VideogularComponent } from 'vbrick-player-src/videogular/Videogular.Component';
import { userAgentUtil } from 'vbrick-player-src/UserAgentUtil';
import { VgStates } from 'vbrick-player-src/videogular/VgStates';

import { IRevConnectInfo } from '@vbrick/vbrick-rev-connect/src/IRevConnectInfo';

import { HlsPeerPlayer } from './HlsPeerPlayer';
import { IConfigExtended } from './IHlsJsExtended';
import { MULTICAST_MAX_RETRIES, multicastXhrConfig } from './MulticastHlsUtil';
import { retainQueryStr } from './UrlUtils';

export const VG_HLS_PLAYER_CROSS_ORIGIN_ERROR = 'hlsCrossOriginError';

export class VgHlsPlayer extends HlsPeerPlayer {
	private retryCount: number;

	constructor(src: string, videoElement: HTMLVideoElement, API: VideogularComponent, isLive: boolean, hlsJsConfigOverrides: Partial<IConfigExtended>, private isMulticast: boolean, revConnectInfo: IRevConnectInfo) {
		super(isMulticast ? retainQueryStr(src, ['reorderpackets']) : src, videoElement, API, isLive, Object.assign({
			xhrConfig: isMulticast ? multicastXhrConfig(src) : null,
			manifestLoadPolicy: {
				default: {
					maxTimeToFirstByteMs: Infinity,
					maxLoadTimeMs: 20000,
					timeoutRetry: {
						maxNumRetry: isMulticast ? 1 : 5,
						retryDelayMs: 0,
						maxRetryDelayMs: 0
					},
					errorRetry: {
						maxNumRetry: isMulticast ? 1 : 5,
						retryDelayMs: 1000,
						maxRetryDelayMs: 8000
					}
				}
			},
			fragLoadPolicy: {
				default: {
					maxTimeToFirstByteMs: 10000,
					maxLoadTimeMs: 10000,
					timeoutRetry: {
						maxNumRetry: 4,
						retryDelayMs: 250,
						maxRetryDelayMs: 0
					},
					errorRetry: {
						maxNumRetry: isMulticast ? 11 : 6, //VBM needs more retries.
						retryDelayMs: 250,
						maxRetryDelayMs: 8000
					}
				}
			}
		}, hlsJsConfigOverrides), false, revConnectInfo);

		if (isMulticast) {
			this.retryCount = 0;

			this.applyMulticastListeners();
		}
	}

	public destroy(): void {
		this.removeMulticastListeners();

		super.destroy();
	}

	protected onFatalError(timeOfError: number, data: ErrorData): void {
		switch (data.details) {
			case this.Hls.ErrorDetails.MANIFEST_LOAD_ERROR:
			case this.Hls.ErrorDetails.LEVEL_LOAD_ERROR:
				if (this.isMulticast && data.context.type === 'manifest' && ++this.retryCount < MULTICAST_MAX_RETRIES) {
					this.player.loadSource(this.hlsUrl);
					/* eslint-disable */
					this.streamController['_forceStartLoad'] = true;
					return;
				} else if (!this.isMulticast && !(data as any).loader.loader.status) {
					this.emit(VG_HLS_PLAYER_CROSS_ORIGIN_ERROR);
					return;
				}
		}

		super.onFatalError(timeOfError, data);
	}

	private applyMulticastListeners(): void {
		this.videoElement.addEventListener('pause', this.onMulticastPause);
	}

	private onMulticastPause = () => {
		// workaround for Safari sometimes getting thrown into a paused state rather than erroring as expected
		window.setTimeout(() => {
			if (this.samePlaylistCount && this.isBuffering && this.API.currentState === VgStates.PAUSE) {
				console.warn('Caught multicast paused');
				this.play(false);
			}
		}, 1000);
	};

	private removeMulticastListeners(): void {
		this.videoElement.removeEventListener('pause', this.onMulticastPause);
	}
}
